import { useEffect, useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import {
  WeeklyHoursEditor,
  type WeeklyHoursDay,
} from '@/components/organisms/availability/WeeklyHoursEditor';
import {
  useLeadRepSchedule,
  useUpdateLeadRepSchedule,
  type WeeklyDay,
} from '@/hooks/leads/useLeadRepSchedule';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';

/** user-level WeeklyDay (isWorking) ⇆ editor's WeeklyHoursDay (isAvailable). */
function toEditorDays(days: WeeklyDay[]): WeeklyHoursDay[] {
  return days.map((d) => ({
    dayOfWeek: d.dayOfWeek,
    isAvailable: d.isWorking,
    timeRanges: d.timeRanges,
  }));
}
function toApiDays(days: WeeklyHoursDay[]): WeeklyDay[] {
  return days.map((d) => ({
    dayOfWeek: d.dayOfWeek,
    isWorking: d.isAvailable,
    timeRanges: d.timeRanges,
  }));
}

/**
 * Per-rep weekly working-hours editor. Reuses the same WeeklyHoursEditor grid
 * as the technician form, but reads/writes USER-LEVEL availability (the single
 * source the leads engine + scheduler share). When "honor weekly hours" is on,
 * the engine skips a rep who's off-shift here.
 */
export function RepWeeklyHoursDrawer({
  userId,
  userName,
  canConfigure,
  onClose,
}: {
  userId: string | null;
  userName: string;
  canConfigure: boolean;
  onClose: () => void;
}) {
  const open = Boolean(userId);
  const scheduleQuery = useLeadRepSchedule(userId);
  const updateSchedule = useUpdateLeadRepSchedule();
  const companyTimezone = useCompanyTimezone() ?? 'UTC';

  const [days, setDays] = useState<WeeklyHoursDay[]>([]);
  const [timezone, setTimezone] = useState(companyTimezone);

  // Load server state into the local draft whenever the fetched schedule
  // changes (open, refetch) so the grid reflects what's persisted.
  useEffect(() => {
    if (scheduleQuery.data) {
      setDays(toEditorDays(scheduleQuery.data.days));
      // An unconfigured rep has no meaningful stored timezone (backend defaults
      // to UTC) — seed the company timezone so "honor weekly hours" evaluates
      // 09:00–17:00 in local time, not UTC. Keep the stored tz once configured.
      const hasSchedule = scheduleQuery.data.days.some((day) => day.isWorking);
      setTimezone(hasSchedule ? scheduleQuery.data.timezone : companyTimezone);
    }
  }, [scheduleQuery.data, companyTimezone]);

  function handleSave() {
    if (!userId) return;
    updateSchedule.mutate({ userId, timezone, days: toApiDays(days) }, { onSuccess: onClose });
  }

  return (
    <Sheet open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <SheetContent className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Weekly hours — {userName}</SheetTitle>
          <SheetDescription>
            The days and times this rep is on-shift. Used by the leads engine when “Honor weekly
            working hours” is on, and shared with scheduling.
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-1 py-2">
          {scheduleQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <p className="mb-2 text-xs text-muted-foreground">Timezone: {timezone}</p>
              <WeeklyHoursEditor
                days={days}
                onChange={setDays}
                disabled={!canConfigure || updateSchedule.isPending}
              />
            </>
          )}
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canConfigure || scheduleQuery.isLoading || updateSchedule.isPending}
          >
            {updateSchedule.isPending ? 'Saving…' : 'Save hours'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
